import CoverPage from "../pages/CoverPage/CoverPage";
import PillarsOfLongevityPage from "../pages/PillarsOfLongevityPage/PillarsOfLongevityPage";
import OverviewMetricBarGraphPage from "../pages/OverviewMetricBarGraphPage/OverviewMetricBarGraphPage";
import CoreMetricsPage from "../pages/CoreMetricsPage/CoreMetricsPage";
import WellnessHealthHistoryPage from "../pages/WellnessHealthHistoryPage/WellnessHealthHistoryPage";
import MetricPage from "../pages/MetricPage/MetricPage";
import RiskMetricPage from "../pages/MetricPage/RiskMetricPage";
import CaloricBalancePage from "../pages/CaloricBalance/CaloricBalancePage";
import MacronutrientBalancePage from "../pages/MacronutrientBalancePage/MacronutrientBalancePage";
import TestingSchedulePage from "../pages/TestingSchedulePage/TestingSchedulePage";
import AuxiliaryTreatmentsPageV02 from "../pages/AuxiliaryTreatmentsPage_V02/AuxiliaryTreatmentsPageV02";
import UserServicesPage from "../pages/UserServicesPage/UserServicesPage";
import React from "react";
import BiologicalAgePage from "../pages/BiologicalAgePageOld/BiologicalAgePage";
import { SvgConverterType } from "./Report";
import RMRCheatsheetPage from "../pages/RMRCheatsheet/RMRCheatsheetPage";
import { NewReportData } from "../../new-reports/types/newReportTypes";
import ScaledNewReportPages from "../components/ScaledNewReportPages";
import { ReportLang } from "../i18n/reportLang";
import { getWhiteLabelTheme } from "../whiteLabel/themes";
import WhiteLabelReportRMR from "./WhiteLabelReportRMR";

type Props = {
  data: any;
  newData: NewReportData | undefined;
  svgConverter: SvgConverterType;
  reportType: string;
  isCreatingPDF: boolean;
  /** Language for frontend-owned copy in white-label variants. */
  lang: ReportLang;
};
const ReportRMR = ({
  data,
  newData,
  svgConverter,
  reportType,
  isCreatingPDF,
  lang,
}: Props) => {
  // console.log(data);

  // White-label variants share none of the default page flow, so they render a
  // separate shell rather than a page order in getComponentOrder(). Resolved from
  // the theme registry, which keeps per-client strings out of this file. Placed
  // above the HEALTH_HISTORY loop below, which derefs .SECTIONS unguarded.
  const whiteLabelTheme = getWhiteLabelTheme(data.customType);
  if (whiteLabelTheme) {
    return (
      <WhiteLabelReportRMR
        data={data}
        theme={whiteLabelTheme}
        lang={lang}
        svgConverter={svgConverter}
        reportType={reportType}
        isCreatingPDF={isCreatingPDF}
      />
    );
  }

  let wellnessHealthHistoryPageHasContext = false;
  for (
    let i = 0;
    i <
    data.PAGES.find((page: any) => page.PAGE_TYPE === "HEALTH_HISTORY").SECTIONS
      .length;
    i++
  ) {
    if (
      data.PAGES.find((page: any) => page.PAGE_TYPE === "HEALTH_HISTORY")
        .SECTIONS[i].TEXT !== null
    ) {
      wellnessHealthHistoryPageHasContext = true;
    }
  }

  // delete when deployed new VO2 max chart
  const biaxialLineChartDataHandler = () => {
    const biaxialLineChartData = {
      gender: data.GENDER,
      lines_men: [
        {
          x: 15,
          area0: [15, 37],
          area1: [37, 42],
          area2: [42, 47],
          area3: [47, 52],
          area4: [52, 65],
        },
        {
          x: 75,
          area0: [15, 21],
          area1: [21, 24],
          area2: [24, 27],
          area3: [27, 30],
          area4: [30, 65],
        },
        { x: data.AGE, lineUser: data.VO2PEAK },
      ],
      lines_women: [
        {
          x: 15,
          area0: [15, 33],
          area1: [33, 38],
          area2: [38, 42],
          area3: [42, 47],
          area4: [47, 65],
        },
        {
          x: 75,
          area0: [15, 17],
          area1: [17, 20],
          area2: [20, 23],
          area3: [23, 25],
          area4: [25, 65],
        },
        { x: data.AGE, lineUser: data.VO2PEAK },
      ],
    };
    return biaxialLineChartData;
  };

  // Flatten METRICS from ORGANS
  const flattenedMetricsArray = Object.values(
    data.PAGES.find((page: any) => page.PAGE_TYPE === "OVERVIEW").ORGANS
  ).flatMap((organ: any) => [...organ.METRICS]);

  // "restore1" is a variant of "restore": it inherits every restore behavior
  // and additionally shows the Category column in the RMR Cheatsheet table.
  // Treat both the same everywhere restore is detected.
  const isRestore =
    data.customType === "restore" || data.customType === "restore1";

  const getComponentOrder = () => {
    const defaultOrder = [
      "cover",
      "pillars",
      "overview",
      "intro",
      "healthHistory",
      "biologicalAge",
      // "userServices",
      "metrics",
      "frankMetrics",
      "riskMetrics",
      "caloricBalance",
      "macronutrientBalance",
      "testingSchedule",
      // "auxiliaryTreatments",
      "newReportPages",
    ];

    const restoreOrder = [
      "cover",
      "pillars",
      "overview",
      "healthHistory",
      "biologicalAge",
      "caloricBalance",
      "cheatsheet",
      "metrics",
      "riskMetrics",
    ];

    return isRestore ? restoreOrder : defaultOrder;
  };

  const componentMap = {
    cheatsheet: () =>
      isRestore &&
      data.CUSTOM_DATA && (
        <RMRCheatsheetPage
          data={data.CUSTOM_DATA}
          customReportType={data.customType}
          showRestoreServiceSection
        />
      ),
    cover: () => (
      <CoverPage
        data={data.PAGES.find((page: any) => (page.PAGE_TYPE = "COVER"))}
        trainerName={data.TRAINER}
        trainerMail={data.TRAINER_MAIL}
        onSvgConverter={svgConverter}
        reportType={reportType}
        isRestoreReport={isRestore}
        isRetoReport={data.customType === "reto"}
        isNaturalSlimReport={data.customType === "naturalslim"}
        isEvityReport={data.customType === "evity"}
        isAlluraReport={data.customType === "allura"}
        isDiPietroHealth={data.customType === "diPietroHealth"}
        isFourthStreetMedical={data.customType === "4thstreetmedical"}
        gender={data.GENDER}
        isGNCReport={false}
      />
    ),
    pillars: () => (
      <PillarsOfLongevityPage
        data={data.PAGES.find((page: any) => page.PAGE_TYPE === "PILLARS")}
        gender={data.GENDER}
        reportType={reportType}
        isRestoreReport={isRestore}
        isRetoReport={data.customType === "reto"}
        isNaturalSlimReport={data.customType === "naturalslim"}
        isEvityReport={data.customType === "evity"}
        isFourthStreetMedical={data.customType === "4thstreetmedical"}
      />
    ),
    overview: () => (
      <OverviewMetricBarGraphPage
        data={data.PAGES.find((page: any) => page.PAGE_TYPE === "OVERVIEW")}
        onSvgConverter={svgConverter}
        reportType={
          reportType === "10" || reportType === "12" || reportType === "14"
            ? "isAMR"
            : reportType === "18"
            ? "isFrank_AMR"
            : reportType === "17"
            ? "isFrank_RMR"
            : "isRMR"
        }
        isCreatingPDF={isCreatingPDF}
        isEvityReport={data.customType === "evity"}
      />
    ),
    intro: () =>
      data.PAGES.find((page: any) => page.PAGE_TYPE === "INTRO") && (
        <CoreMetricsPage
          data={data.PAGES.find((page: any) => page.PAGE_TYPE === "INTRO")}
          reportType={reportType}
          isRestoreReport={isRestore}
          isEvityReport={data.customType === "evity"}
        />
      ),
    healthHistory: () =>
      wellnessHealthHistoryPageHasContext && (
        <WellnessHealthHistoryPage
          data={data.PAGES.find(
            (page: any) => page.PAGE_TYPE === "HEALTH_HISTORY"
          )}
          isEvityReport={data.customType === "evity"}
        />
      ),
    biologicalAge: () =>
      data.PAGES.find((page: any) => page.PAGE_TYPE === "BIOLOGICAL_AGE") && (
        <BiologicalAgePage
          data={data.PAGES.find(
            (page: any) => page.PAGE_TYPE === "BIOLOGICAL_AGE"
          )}
          onSvgConverter={svgConverter}
          biaxialLineChartData={biaxialLineChartDataHandler()}
          dataNew={
            data.PAGES.find((page: any) => page.PAGE_TYPE === "OVERVIEW").NAVBAR
          }
          showVO2MaxChart={
            data.customType === "thedripbar" ||
            data.RMR_VO2MAX_PREDICTED === 1 ||
            isRestore
          }
          isEvityReport={data.customType === "evity"}
          isDripBarReport={data.customType === "thedripbar"}
          isGNCReport={false}
        />
      ),
    // userServices: () =>
    //   data.USERSERVICES === 1 &&
    //   data.PAGES.find((page: any) => page.PAGE_TYPE === "USERSERVICES") && (
    //     <UserServicesPage
    //       data={data.PAGES.find(
    //         (page: any) => page.PAGE_TYPE === "USERSERVICES"
    //       )}
    //       isRestoreReport={isRestore}
    //       isEvityReport={data.customType === "evity"}
    //       isCreatingPDF={isCreatingPDF}
    //       customShare={data.custom_share}
    //     />
    //   ),
    metrics: () =>
      flattenedMetricsArray.length > 0 && (
        <MetricPage
          data={flattenedMetricsArray}
          navBarData={
            data.PAGES.find((page: any) => page.PAGE_TYPE === "OVERVIEW").NAVBAR
          }
          qrCodeData={
            data.PAGES.find((page: any) => page.PAGE_TYPE === "OVERVIEW").QRCODE
          }
          isRetoReport={data.customType === "reto"}
          isNaturalSlimReport={data.customType === "naturalslim"}
          isSyncLifeReport={data.customType === "syncLife"}
          isEvityReport={data.customType === "evity"}
          isGNCReport={false}
          isAlluraReport={data.customType === "allura"}
          isMasonCollinsReport={data.customType === "masonCollins"}
        />
      ),
    frankMetrics: () =>
      data.PAGES.find((page: any) => page.PAGE_TYPE === "OVERVIEW")
        .FRANK_METRICS.length > 0 && (
        <MetricPage
          data={
            data.PAGES.find((page: any) => page.PAGE_TYPE === "OVERVIEW")
              .FRANK_METRICS
          }
          navBarData={
            data.PAGES.find((page: any) => page.PAGE_TYPE === "OVERVIEW").NAVBAR
          }
          qrCodeData={
            data.PAGES.find((page: any) => page.PAGE_TYPE === "OVERVIEW").QRCODE
          }
        />
      ),
    riskMetrics: () =>
      data.PAGES.find((page: any) => page.PAGE_TYPE === "OVERVIEW").ORGANS.CELLS
        .RISK_METRICS.length > 0 && (
        <RiskMetricPage
          data={data.PAGES.find((page: any) => page.PAGE_TYPE === "OVERVIEW")}
          onSvgConverter={svgConverter}
          isRetoReport={data.customType === "reto"}
          isNaturalSlimReport={data.customType === "naturalslim"}
          isSyncLifeReport={data.customType === "syncLife"}
          isEvityReport={data.customType === "evity"}
          isGNCReport={false}
          isMasonCollinsReport={data.customType === "masonCollins"}
        />
      ),
    caloricBalance: () => (
      <CaloricBalancePage
        data={data.PAGES.find(
          (page: any) => page.PAGE_TYPE === "CALORIC_BALANCE"
        )}
        onSvgConverter={svgConverter}
        isRestoreReport={isRestore}
        isEvityReport={data.customType === "evity"}
      />
    ),
    macronutrientBalance: () => (
      <MacronutrientBalancePage
        data={data.PAGES.find(
          (page: any) => page.PAGE_TYPE === "MACRO_BALANCE"
        )}
        onSvgConverter={svgConverter}
        isEvityReport={data.customType === "evity"}
        isFountainLife={data.customType === "fountainLife"}
        isGNCReport={false}
      />
    ),
    testingSchedule: () => (
      <TestingSchedulePage
        data={data.PAGES.find(
          (page: any) => page.PAGE_TYPE === "TESTING_SCHEDULE"
        )}
        onSvgConverter={svgConverter}
        isRestoreReport={isRestore}
        isEvityReport={data.customType === "evity"}
        isFountainLife={data.customType === "fountainLife"}
        isGNCReport={false}
      />
    ),
    // auxiliaryTreatments: () =>
    //   data.USERSERVICES === 0 &&
    //   data.PAGES.find((page: any) => page.PAGE_TYPE === "SERVICES") && (
    //     <AuxiliaryTreatmentsPageV02
    //       data={data.PAGES.find((page: any) => page.PAGE_TYPE === "SERVICES")}
    //       isEvityReport={data.customType === "evity"}
    //     />
    //   ),
    newReportPages: () =>
      newData &&
      newData.pages &&
      newData.pages.some(
        (page) => page.pageType === "services" && page.services.length > 0
      ) && <ScaledNewReportPages newData={newData} />,
  };

  const componentOrder = getComponentOrder();

  return (
    <>
      {componentOrder.map((componentKey) => {
        const Component =
          componentMap[componentKey as keyof typeof componentMap];
        return Component ? (
          <React.Fragment key={componentKey}>{Component()}</React.Fragment>
        ) : null;
      })}
    </>
  );
};

export default ReportRMR;
